// filepath: c:\Users\ManuMathew\work\Modelling\Modelling Portal\src\Common\Components\MUITable.methods.ts
/**
 * Saves the changes made to the grid by updating the rows and notifying the parent component.
 *
 * @template RowType - The type of the row data in the grid.
 * @param {Dispatch<SetStateAction<boolean>>} setHasUnsavedRows - The state setter for indicating if there are unsaved rows.
 * @param {MutableRefObject<GridApiPremium>} apiRef - The reference to the grid API.
 * @param {MutableRefObject<{
 *   unsavedRows: Record<GridRowId, RowType>,
 *   rowsBeforeChange: Record<GridRowId, RowType>
 * }>} unsavedChangesRef - The reference to the unsaved changes object.
 * @param {(changedRows: Record<number, RowType>) => void} updateTableData - The function to update the table data in the parent component.
 * @param {Dispatch<SetStateAction<boolean>>} setIsSaving - The state setter for indicating if the changes are being saved.
 */
import {
  GridRowId,
  GridRowModelUpdate,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium'
import { Dispatch, MutableRefObject, SetStateAction } from 'react'

export function saveChanges<RowType>(
  setHasUnsavedRows: Dispatch<SetStateAction<boolean>>,
  apiRef: MutableRefObject<GridApiPremium>,
  unsavedChangesRef: MutableRefObject<{
    unsavedRows: Record<GridRowId, RowType>
    rowsBeforeChange: Record<GridRowId, RowType>
  }>,
  updateTableData: (changedRows: Record<number, RowType>) => void,
  setIsSaving: Dispatch<SetStateAction<boolean>>
) {
  try {
    setHasUnsavedRows(false)
    apiRef.current.updateRows(
      Object.values(
        unsavedChangesRef.current.unsavedRows
      ) as GridRowModelUpdate[]
    )
    // Sending updated table data to parent component.
    const keysToUpdate = Object.keys(unsavedChangesRef.current.unsavedRows)
    if (keysToUpdate.length > 0) {
      updateTableData({ ...unsavedChangesRef.current.unsavedRows })
    }
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    }
  } catch (error) {
    setIsSaving(false)
  }
}

export function saveChangesToServer<RowType>(
  apiRef: MutableRefObject<GridApiPremium>,
  unsavedChangesRef: MutableRefObject<{
    unsavedRows: Record<GridRowId, RowType>
    rowsBeforeChange: Record<GridRowId, RowType>
  }>,
  updateTableData: (changedRows: Record<number, RowType>) => void,
  undoRedoStack: MutableRefObject<GridRowId[]>,
  undoRedoPonter: MutableRefObject<number>,
  setHasUnsavedRows?: (value: boolean) => void,
  setSelectedRows?: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
) {
  try {
    apiRef.current.updateRows(
      Object.values(
        unsavedChangesRef.current.unsavedRows
      ) as GridRowModelUpdate[]
    )
    // Sending updated table data to parent component.
    const keysToUpdate = Object.keys(unsavedChangesRef.current.unsavedRows)
    if (keysToUpdate.length > 0) {
      updateTableData({ ...unsavedChangesRef.current.unsavedRows })
    }
    unsavedChangesRef.current = {
      unsavedRows: {},
      rowsBeforeChange: {},
    }
    if (setHasUnsavedRows) {
      setHasUnsavedRows(false)
    }
    if (setSelectedRows) {
      setSelectedRows({
        type: 'include',
        ids: new Set<GridRowId>(),
      })
    }
    undoRedoStack.current = []
    undoRedoPonter.current = -1
  } catch (error) {
    console.error(error)
  }
}

export const discardChanges = <RowType>(
  apiRef: MutableRefObject<GridApiPremium>,
  unsavedChangesRef: MutableRefObject<{
    unsavedRows: Record<GridRowId, RowType>
    rowsBeforeChange: Record<GridRowId, RowType>
  }>,
  undoRedoStack: MutableRefObject<GridRowId[]>,
  undoRedoPonter: MutableRefObject<number>,
  setHasUnsavedRows?: (value: boolean) => void
) => {
  apiRef.current.updateRows(
    Object.values(
      unsavedChangesRef.current.rowsBeforeChange
    ) as GridRowModelUpdate[]
  )
  unsavedChangesRef.current = {
    unsavedRows: {},
    rowsBeforeChange: {},
  }
  if (setHasUnsavedRows) {
    setHasUnsavedRows(false)
  }
  undoRedoPonter.current = -1
  undoRedoStack.current = []
}

// Keep the rest of the file as is
